import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setAllGroupUser,
  setGroupUser,
} from "../../../../redux/TaskGroupingSlice";
import { useUsers } from "../../../../utils/reactQuery/users";
import SearchBoard from "./SearchBoard";
import CustomMenu from "../../../../components/Menu";
import SelectAllMenuItem from "./SelectAllBoardMenuItem";
import "../../myTask.scss";
import TeamAccordion from "./TeamAccordion";
import UnassignedUsersAccordion from "./UnassignedUsersAccordion";
import TeamButton from "./TeamButton";
import { useResponsive } from "../../../../hooks/useResponsive";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const Team = React.memo(
  function Team({ team }) {
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const { users } = useUsers();
    const open = Boolean(anchorEl);
    const [openUnassigned, setUnassigned] = useState(false);
    const [expandedPanel, setExpandedPanel] = useState([]);
    const [wasSearching, setWasSearching] = useState(false);
    const isMobile = useResponsive("down", "md");

    // Get user data directly from Redux
    const groupingUser = useSelector(
      (state) => state.groupingUser.allocationUsers
    );
    const user = useMemo(() => {
      return groupingUser?.map((item) => item?.users).flat() || [];
    }, [groupingUser]);


    const allTeams = useMemo(() => {
      return team?.data.data.responseData?.map((team) => ({
        ...team,
        users: team.users.filter((user) => user.active),
      })) || [];
    }, [team?.data.data.responseData]);

    const filteredItems = useMemo(() => {
      if (!users?.data?.data?.responseData) return [];
      
      if (!searchQuery) return users.data.data.responseData;
      
      const matchingTeams = allTeams.filter((team) =>
        team.team.toLowerCase().includes(searchQuery.toLowerCase())
      );
      
      if (matchingTeams.length > 0) {
        const matchingTeamIds = matchingTeams.map(team => team.team_id);
        return users.data.data.responseData.filter((user) =>
          matchingTeamIds.includes(user.team_id)
        );
      }
      
      return users.data.data.responseData.filter((item) =>
        item.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.last_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [users?.data?.data?.responseData, searchQuery, allTeams]);

    const unAssignedTeam = useMemo(() => {
      if (!filteredItems.length) return [];
      
      const activeUsers = filteredItems.filter((el) => el.active);
      const selectedUserIds = new Set(user.map(u => u.user_id));
      
      return activeUsers
        .filter((ele) => !ele.team_id)
        .sort((a, b) => {
          const aSelected = selectedUserIds.has(a.user_id);
          const bSelected = selectedUserIds.has(b.user_id);
          if (aSelected !== bSelected) {
            return bSelected - aSelected;
          }
          return a.first_name.localeCompare(b.first_name);
        });
    }, [filteredItems, user]);

    const allUser = useMemo(() => {
      return filteredItems?.filter((el) => el.active) || [];
    }, [filteredItems]);

    const handleExpandAccordianChange = useCallback(
      (teamId) => (event, isExpanded) => {
        setExpandedPanel(prev => 
          prev.map((element) => 
            element?.team_id === teamId 
              ? { ...element, isExpand: isExpanded }
              : element
          )
        );
      },
      []
    );

    useEffect(() => {
      if (allTeams.length) {
        setExpandedPanel(
          allTeams.map((element) => ({ 
            team_id: element.team_id, 
            isExpand: false 
          }))
        );
      }
    }, [allTeams]);

    useEffect(() => {
      if (searchQuery) {
        setWasSearching(true);
        
        if (allTeams.length) {
          const expandData = allTeams.map((element) => {
            const hasMatchingUsers = element.users.some((usr) =>
              filteredItems.some((user) => user.user_id === usr.user_id)
            );
            return { 
              team_id: element.team_id, 
              isExpand: hasMatchingUsers
            };
          });
          setExpandedPanel(expandData);
        }
        
        if (unAssignedTeam.length > 0) {
          setUnassigned(true);
        }
      } else if (wasSearching) {
        setWasSearching(false);
        setUnassigned(false);
        
        if (allTeams.length) {
          setExpandedPanel(
            allTeams.map((element) => ({ 
              team_id: element.team_id, 
              isExpand: false 
            }))
          );
        }
      }
    }, [searchQuery, allTeams, filteredItems, unAssignedTeam.length, wasSearching]);

    const setTeam = useCallback((teamUser, isChecked) => {
      dispatch(setAllGroupUser([]));

      if (isChecked) {
        user.forEach((element) => {
          if (element.team_id !== teamUser.team_id) {
            dispatch(setGroupUser(element));
          }
        });
      } else {
        teamUser.users.forEach((element) => {
          dispatch(setGroupUser(element));
        });

        user.forEach((element) => {
          if (element.team_id !== teamUser.team_id) {
            dispatch(setGroupUser(element));
          }
        });
      }
    }, [dispatch, user]);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      setSearchQuery("");
    }, []);

    const selectAll = useCallback(() => {
      dispatch(
        setAllGroupUser(
          user.length === allUser.length
            ? []
            : [{ team: "Multiple", users: allUser }]
        )
      );
    }, [dispatch, user.length, allUser.length, allUser]);

    return (
      <>
        {isMobile ? (
          <Accordion className="filter-accordion">
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header">
              <Typography component="span">
                By Users{" "}
                <span className="filter-count">
                  {user.length === 0 ? "" : user.length}
                </span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "0px 12px 12px" }}>
              <SearchBoard
                onSearch={(val) => setSearchQuery(val)}
                searchQuery={searchQuery}
              />
              <div className="filter-accordion-content">
                {!searchQuery && (
                  <SelectAllMenuItem
                    selectAll={selectAll}
                    className={"select-all-menu-item"}
                    label={"Select All Users"}
                    checked={user.length == allUser.length}
                  />
                )}
                {allTeams.map((team, idx) =>
                  team &&
                  team?.users &&
                  team?.users.length &&
                  team.users.some((usr) =>
                    filteredItems.some((user) => user.user_id === usr.user_id)
                  ) ? (
                    <TeamAccordion
                      key={`team-${team.team_id}`}
                      team={team}
                      user={user}
                      expandedPanel={expandedPanel}
                      handleExpandAccordianChange={handleExpandAccordianChange}
                      setTeam={setTeam}
                      filteredItems={filteredItems}
                      searchQuery={searchQuery}
                    />
                  ) : (
                    ""
                  )
                )}
                {unAssignedTeam.length > 0 && (
                  <UnassignedUsersAccordion
                    unAssignedTeam={unAssignedTeam}
                    setUnassigned={setUnassigned}
                    user={user}
                    searchQuery={searchQuery}
                    openUnassigned={openUnassigned}
                  />
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        ) : (
          <>
            <TeamButton user={user} setAnchorEl={setAnchorEl} />
            <CustomMenu
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              isApplyCustomMenuCss={false}
              className="custom-menu-box-shadow-none">
              <Box className="search-box">
                <SearchBoard
                  onSearch={(val) => setSearchQuery(val)}
                  searchQuery={searchQuery}
                />
              </Box>
              {!searchQuery && (
                <SelectAllMenuItem
                  selectAll={selectAll}
                  className={"select-all-menu-item"}
                  label={"Select All Users"}
                  checked={user.length == allUser.length}
                />
              )}
              {allTeams.map((team, idx) =>
                team &&
                team?.users &&
                team?.users.length &&
                team.users.some((usr) =>
                  filteredItems.some((user) => user.user_id === usr.user_id)
                ) ? (
                  <TeamAccordion
                    key={`team-${team.team_id}`}
                    team={team}
                    user={user}
                    expandedPanel={expandedPanel}
                    handleExpandAccordianChange={handleExpandAccordianChange}
                    setTeam={setTeam}
                    filteredItems={filteredItems}
                    searchQuery={searchQuery}
                  />
                ) : (
                  ""
                )
              )}
              {unAssignedTeam.length > 0 && (
                <UnassignedUsersAccordion
                  unAssignedTeam={unAssignedTeam}
                  setUnassigned={setUnassigned}
                  user={user}
                  searchQuery={searchQuery}
                  openUnassigned={openUnassigned}
                />
              )}
            </CustomMenu>
          </>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    // For React.memo, return true to prevent re-render, false to allow re-render
    // We only need to check if team data has changed since user data is now from Redux

    // Check if team data has changed
    const teamDataChanged =
      prevProps.team?.data?.data?.responseData?.length !==
        nextProps.team?.data?.data?.responseData?.length ||
      JSON.stringify(prevProps.team?.data?.data?.responseData) !==
        JSON.stringify(nextProps.team?.data?.data?.responseData);

    // Return false to trigger re-render when team data has changed
    return !teamDataChanged;
  }
);

export default Team;
